import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, BookOpen, LogOut, Play, Save, Send, ShieldCheck, User } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { niveisEscolares, objetivosEstudo } from '../constants/profileOptions'

function PerfilPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { usuario, accessToken, atualizarUsuario, logout } = useAuthStore()
  const [perfilForm, setPerfilForm] = useState({ nome: '', nivel_escolar: '', objetivo: '' })

  const { data: perfilAtual } = useQuery({
    queryKey: ['usuario', 'me'],
    queryFn: async () => (await api.get('/users/me')).data.usuario,
    enabled: Boolean(accessToken)
  })

  const { data: historico = [] } = useQuery({
    queryKey: ['historico-videoaulas'],
    queryFn: async () => (await api.get('/historico-videoaulas')).data.historico,
    enabled: Boolean(accessToken)
  })

  const { data: recomendacoes = [] } = useQuery({
    queryKey: ['recomendacoes'],
    queryFn: async () => (await api.get('/recomendacoes')).data.recomendacoes,
    enabled: Boolean(accessToken)
  })

  const perfilParaFormulario = perfilAtual || usuario

  useEffect(() => {
    if (!perfilParaFormulario) return
    setPerfilForm({
      nome: perfilParaFormulario.nome || '',
      nivel_escolar: perfilParaFormulario.nivel_escolar || '',
      objetivo: perfilParaFormulario.objetivo || ''
    })
    if (perfilAtual) atualizarUsuario(perfilAtual)
  }, [perfilAtual, perfilParaFormulario, atualizarUsuario])

  const salvarPerfil = useMutation({
    mutationFn: async () => {
      const payload = {
        nome: perfilForm.nome.trim(),
        nivel_escolar: perfilForm.nivel_escolar || null,
        objetivo: perfilForm.objetivo || null
      }
      return (await api.put('/users/me', payload)).data.usuario
    },
    onSuccess: perfil => {
      atualizarUsuario(perfil)
      queryClient.setQueryData(['usuario', 'me'], perfil)
      toast.success('Dados pessoais atualizados!')
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível atualizar o perfil.'))
  })

  if (!usuario) {
    return (
      <div className="card text-center py-8">
        <p className="text-sm text-gray-600 mb-3">Entre para ver seu histórico e recomendações.</p>
        <Link to="/login" className="btn-primary inline-block">Entrar</Link>
      </div>
    )
  }

  const initials = usuario.nome.split(' ').slice(0, 2).map(part => part[0]).join('').toUpperCase()
  const podeRecomendar = usuario.tipo_perfil === 'professor' || usuario.instituicoes?.some(
    item => ['professor', 'gestor'].includes(item.papel) && item.status === 'ativo'
  )
  const perfilLabel = usuario.tipo_perfil === 'admin'
    ? 'Administrador'
    : usuario.tipo_perfil === 'professor' ? 'Professor' : 'Aluno'
  const perfilStyle = usuario.tipo_perfil === 'admin'
    ? 'bg-emerald-50 text-emerald-700'
    : usuario.tipo_perfil === 'professor'
      ? 'bg-violet-50 text-violet-700'
      : 'bg-blue-50 text-blue-600'

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center gap-4 rounded-2xl border border-gray-100 p-4 lg:p-5">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-xl font-medium">{initials}</div>
        <div>
          <h1 className="text-lg font-medium lg:text-xl">{usuario.nome}</h1>
          <p className="text-sm text-gray-500">{usuario.email}</p>
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${perfilStyle}`}>
            {usuario.tipo_perfil === 'admin' && <ShieldCheck size={11} />}
            {perfilLabel}
          </span>
        </div>
      </div>

      <section className="card max-w-3xl">
        <div className="mb-4 flex items-center gap-2">
          <User size={17} />
          <div>
            <h2 className="text-sm font-medium">Dados pessoais</h2>
            <p className="text-xs text-gray-500">Atualize as informações usadas para organizar sua experiência de estudo.</p>
          </div>
        </div>
        <form className="space-y-3" onSubmit={event => { event.preventDefault(); salvarPerfil.mutate() }}>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-500">Nome</span>
            <input className="input-field" value={perfilForm.nome} onChange={event => setPerfilForm(current => ({ ...current, nome: event.target.value }))} required maxLength={100} autoComplete="name" />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-500">E-mail</span>
            <input className="input-field opacity-70" value={usuario.email} readOnly aria-readonly="true" />
            <span className="block text-[11px] text-gray-400">O e-mail da conta não pode ser alterado por aqui.</span>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs font-medium text-gray-500">Nível de escolaridade</span>
              <select className="input-field" value={perfilForm.nivel_escolar} onChange={event => setPerfilForm(current => ({ ...current, nivel_escolar: event.target.value }))}>
                <option value="">Não informado</option>
                {niveisEscolares.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-gray-500">Objetivo principal</span>
              <select className="input-field" value={perfilForm.objetivo} onChange={event => setPerfilForm(current => ({ ...current, objetivo: event.target.value }))}>
                <option value="">Não informado</option>
                {objetivosEstudo.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
          </div>
          <button className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto" disabled={salvarPerfil.isLoading || !perfilForm.nome.trim()}>
            <Save size={15} /> {salvarPerfil.isLoading ? 'Salvando...' : 'Salvar dados pessoais'}
          </button>
        </form>
      </section>

      {podeRecomendar && (
        <section className="card max-w-3xl border-violet-100 bg-violet-50/50 dark:border-violet-950 dark:bg-violet-950/20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Send size={18} className="mt-0.5 shrink-0 text-violet-700 dark:text-violet-300" />
              <div>
                <h2 className="text-sm font-medium">Recomendações para os alunos</h2>
                <p className="mt-1 text-xs leading-5 text-gray-500">Envie links importantes e acompanhe as recomendações já compartilhadas.</p>
              </div>
            </div>
            <Link to="/recomendar" className="btn-primary inline-flex shrink-0 items-center justify-center gap-1.5 px-4 py-2 text-xs">
              Abrir área do professor <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {recomendacoes.length > 0 && (
        <section>
          <h2 className="text-sm font-medium mb-3">Recomendações dos professores</h2>
          <div className="grid gap-2 md:grid-cols-2">
            {recomendacoes.map(item => (
              <Link key={item.id} to={`/recomendacao/${item.id}`} className="card flex items-center gap-3">
                <Play size={17} className="text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.titulo}</p>
                  <p className="text-xs text-gray-500">Por {item.professor?.nome}{item.turma ? ` · ${item.turma.nome}` : item.instituicao ? ` · ${item.instituicao.nome}` : ''}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={17} className="text-gray-400" />
          <h2 className="text-sm font-medium">Aulas assistidas ({historico.length})</h2>
        </div>
        {historico.length ? (
          <div className="grid gap-2 md:grid-cols-2">
            {historico.map(item => (
              <Link key={item.id} to={`/historico/${item.id}`} className="card block">
                <p className="text-sm font-medium truncate">{item.titulo}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.canal || 'YouTube'}</p>
                {item.resumo && <p className="text-xs text-emerald-600 mt-1">Resumo e exercícios disponíveis</p>}
              </Link>
            ))}
          </div>
        ) : (
          <p className="card text-sm text-gray-500">As videoaulas abertas aparecerão aqui.</p>
        )}
      </section>

      <button
        type="button"
        onClick={() => { logout(); navigate('/login') }}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-100 py-3 text-sm font-medium text-red-600 hover:bg-red-50 sm:max-w-xs"
      >
        <LogOut size={16} /> Sair da conta
      </button>
    </div>
  )
}

export default PerfilPage
